/**
 * LOT-UI-03b.1 — Badge magasin session (header, DS Remobiles).
 */
import React, { useState } from 'react';
import { ChevronDown, Globe2, Store, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { usePermissions } from '../contexts/PermissionsContext';

function hasOngoingWork() {
  try {
    const cart = JSON.parse(localStorage.getItem('pos_cart') || '[]');
    if (Array.isArray(cart) && cart.length > 0) return true;
  } catch (_) { /* noop */ }
  return false;
}

export const MagasinSessionBadge = () => {
  const {
    isSuper, isAdminMag, assignedMagasins, allMagasins,
    currentSessionMagasin, setSessionMagasin, getMagasinColor, getMagasinName,
  } = usePermissions();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // code en cours de bascule

  // admin_magasin peut encaisser sur tout magasin actif (mode vendeur hors assignation).
  const candidates = allMagasins;
  if (candidates.length <= 1 && !isSuper) return null;

  const code = currentSessionMagasin;
  const isAll = !code;
  const accent = isAll ? 'var(--rm-success)' : (getMagasinColor(code) || 'var(--rm-gray-500)');
  const name = isAll ? 'Tous magasins' : getMagasinName(code);

  // ISOLATION — la bascule appelle select-active côté serveur (nouveau JWT lié à la
  // session) puis rafraîchit. On attend le résultat pour fermer/feedback et refuser
  // fermement une boutique hors périmètre (403).
  const handlePick = async (newCode) => {
    const target = newCode === 'ALL' ? '' : newCode;
    if (target === currentSessionMagasin) { setOpen(false); return; }
    if (hasOngoingWork()) {
      const ok = window.confirm(
        `Tu as un panier en cours sur ${currentSessionMagasin || 'Tous'}. ` +
        `Changer pour ${newCode || 'Tous'} va le mettre en pause. Confirmer ?`
      );
      if (!ok) { setOpen(false); return; }
    }
    setBusy(newCode);
    try {
      const res = await setSessionMagasin(target);
      if (res && res.ok === false) {
        toast.error(res.error || 'Changement de boutique refusé');
      } else if (res && res.global) {
        toast.success('Vue globale (réseau) activée');
      } else if (res) {
        toast.success(`Boutique active : ${res.magasin || target}`);
      }
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  return (
    <div className="relative" data-testid="magasin-session-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 min-h-[44px] rounded-xl border font-semibold text-xs sm:text-sm transition hover:bg-white/[0.03]"
        style={{
          color: 'var(--rm-white)',
          borderColor: 'var(--rm-border-subtle)',
          background: 'var(--rm-bg-panel)',
          boxShadow: `inset 3px 0 0 ${accent}`,
        }}
      >
        {isAll ? <Globe2 className="w-4 h-4 text-[var(--rm-success)]" /> : <Store className="w-4 h-4" />}
        <span>{isAll ? 'Tous' : code}</span>
        <span className="hidden sm:inline text-[var(--rm-gray-400)] normal-case font-normal truncate max-w-[120px]">{name}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[var(--rm-gray-500)]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute right-0 top-full mt-1 z-50 min-w-[240px] rounded-xl shadow-2xl py-1 overflow-hidden"
            style={{ background: 'var(--rm-bg-panel)', border: '1px solid var(--rm-border-subtle)' }}
          >
            {isSuper && (
              <button
                type="button"
                onClick={() => handlePick('ALL')}
                data-testid="magasin-session-switch-all"
                className="w-full flex items-center gap-2 px-3 py-3 min-h-[44px] text-sm text-[var(--rm-success-foreground)] hover:bg-[var(--rm-success-muted)]"
              >
                <Globe2 className="w-4 h-4 text-[var(--rm-success)]" /> Tous (vue globale)
              </button>
            )}
            {candidates.map((m) => {
              const mAccent = m.color || 'var(--rm-gray-500)';
              const selected = m.code === code;
              return (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => handlePick(m.code)}
                  data-testid={`magasin-session-switch-${m.code}`}
                  className={`w-full flex items-center gap-2 px-3 py-3 min-h-[44px] text-sm hover:bg-white/5 text-[var(--rm-gray-100)] ${selected ? 'font-semibold bg-[var(--rm-red-muted)]' : ''}`}
                  style={{ boxShadow: selected ? `inset 3px 0 0 ${mAccent}` : `inset 3px 0 0 transparent` }}
                >
                  {busy === m.code ? <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" /> : <Store className="w-4 h-4 flex-shrink-0" />}
                  <span className="font-bold">{m.code}</span>
                  <span className="text-[var(--rm-gray-400)] normal-case font-normal truncate">— {m.name}</span>
                  {selected ? <span className="ml-auto text-[var(--rm-success)] text-xs">✓</span> : null}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default MagasinSessionBadge;
